import { Box, Button, Flex, Grid, IconArrowRight, Image } from '@cmpsr/components';
import { ReactNode } from 'react';
import IntroWizardsNavBar from '../intro-wizards-nav-bar/intro-wizard-nav-bar';
import WizardProgressIndicator from './components/wizard-progress-indicator/wizard-progress-indicator';
import useIsMobile from '@/hooks/use-is-mobile';
import { AnimatePresence, motion } from 'framer-motion';

type IntroWizardsStepProps = {
  titleNode: ReactNode;
  descriptionNode: ReactNode;
  onNextButtonClick: () => void;
  onBackButtonClick: () => void;
  numberOfSteps: number;
  activeStepIndex: number;
  imageId: 'samples' | 'pills' | 'phone';
  buttonText: string;
};

export default function IntroWizardsStep(props: IntroWizardsStepProps) {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <Box minH="100dvh">
        <IntroWizardsNavBar onBackButtonClick={props.onBackButtonClick} />
        <Image
          srcSet={`/images/intro-wizards/${props.imageId}/${props.imageId}-400w.avif 400w, /images/intro-wizards/${props.imageId}/${props.imageId}-600w.avif 600w, /images/intro-wizards/${props.imageId}/${props.imageId}-800w.avif 800w, /images/intro-wizards/${props.imageId}/${props.imageId}-1000w.avif 1000w, /images/intro-wizards/${props.imageId}/${props.imageId}-1200w.avif 1200w, /images/intro-wizards/${props.imageId}/${props.imageId}-1600w.avif 1600w, /images/intro-wizards/${props.imageId}/${props.imageId}-2000w.avif 2000w`}
          sizes="(max-width: 400px) 400px, (max-width: 600px) 600px, (max-width: 800px) 800px, (max-width: 1000px) 1000px, (max-width: 1200px) 1200px, (max-width: 1600px) 1600px, (min-width: 1601px) 2000px"
          src={`/images/intro-wizards/${props.imageId}/${props.imageId}-400w.avif`}
          alt=""
          width="full"
          minH="80dvw"
          position="absolute"
          top="0"
          objectFit="cover"
        />
        <Flex
          flexDirection="column"
          alignItems={{ base: 'flex-start', lg: 'center' }}
          justifyContent="space-between"
          gap="12"
          position="fixed"
          bottom="0"
          minH="calc(100dvh - 80dvw)"
          mx="auto"
          background="gradient.dark"
          p={{ base: 7, md: 10 }}
          borderTopRadius="radii-6"
          color="text-light"
        >
          <Box>
            <AnimatePresence mode="wait">
              <motion.div
                key={props.activeStepIndex}
                initial={{ opacity: 0, x: 5 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -5 }}
                transition={{ duration: 0.1 }}
              >
                <Box mb={3}>{props.titleNode}</Box>
                {props.descriptionNode}
              </motion.div>
            </AnimatePresence>
          </Box>
          <Flex role="group" alignItems="center" justifyContent="space-between" w="full" aria-label="Slide controls">
            <Flex gap={2}>
              {Array.from({ length: props.numberOfSteps }).map((_, index) => (
                <WizardProgressIndicator key={index} active={index === props.activeStepIndex} />
              ))}
            </Flex>
            <Button
              variant="accent"
              size="l"
              trailingIcon={<IconArrowRight />}
              onClick={props.onNextButtonClick}
              fontSize="16px"
            >
              {props.buttonText}
            </Button>
          </Flex>
        </Flex>
      </Box>
    );
  }

  return (
    <Box minH="100svh" background="gradient.dark" display="flex" flexDirection="column">
      <IntroWizardsNavBar onBackButtonClick={props.onBackButtonClick} showLogo />
      <Flex flex="1" alignItems="center" justifyContent="center" mt="-80px">
        <Grid gridTemplateColumns="375px 375px" gap={12} width="min-content">
          <Image
            srcSet={`/images/intro-wizards/${props.imageId}/${props.imageId}-400w.avif 400w, /images/intro-wizards/${props.imageId}/${props.imageId}-600w.avif 600w, /images/intro-wizards/${props.imageId}/${props.imageId}-800w.avif 800w, /images/intro-wizards/${props.imageId}/${props.imageId}-1000w.avif 1000w, /images/intro-wizards/${props.imageId}/${props.imageId}-1200w.avif 1200w, /images/intro-wizards/${props.imageId}/${props.imageId}-1600w.avif 1600w, /images/intro-wizards/${props.imageId}/${props.imageId}-2000w.avif 2000w`}
            sizes="(max-width: 400px) 400px, (max-width: 600px) 600px, (max-width: 800px) 800px, (max-width: 1000px) 1000px, (max-width: 1200px) 1200px, (max-width: 1600px) 1600px, (min-width: 1601px) 2000px"
            src={`/images/intro-wizards/${props.imageId}/${props.imageId}-400w.avif`}
            alt=""
            maxW="375px"
            h="500px"
            objectFit="cover"
            borderRadius="radii-6"
          />
          <Flex
            flexDirection="column"
            alignItems={{ base: 'flex-start', lg: 'center' }}
            justifyContent="space-between"
            gap="12"
            maxW="375px"
            color="text-light"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={props.activeStepIndex}
                initial={{ opacity: 0, x: 5 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -5 }}
                transition={{ duration: 0.1 }}
              >
                <Box>
                  <Box mb={3}>{props.titleNode}</Box>
                  {props.descriptionNode}
                </Box>
              </motion.div>
            </AnimatePresence>
            <Flex role="group" alignItems="center" justifyContent="space-between" w="full" aria-label="Slide controls">
              <Flex gap={2}>
                {Array.from({ length: props.numberOfSteps }).map((_, index) => (
                  <WizardProgressIndicator key={index} active={index === props.activeStepIndex} />
                ))}
              </Flex>
              <Button
                variant="accent"
                size="l"
                trailingIcon={<IconArrowRight />}
                onClick={props.onNextButtonClick}
                fontSize="16px"
              >
                {props.buttonText}
              </Button>
            </Flex>
          </Flex>
        </Grid>
      </Flex>
    </Box>
  );
}
